import { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import { SingleCard } from "../../layouts";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";

const FORM_ID = "edit-client-dialog-form";

const EMPTY_FORM = {
  client_first_name: "",
  client_last_name: "",
  client_email: "",
  client_mobile_phone_number: "",
  client_birthday: null,
  client_id: "",
  appointment_notify_opt_in: false,
  marketing_opt_in: false,
};

export default function EditClientForm(props = {}) {
  const {
    clientId: clientIdProp,
    onSaved,
    onRequestClose,
  } = props;

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get("clientId");

  const effectiveClientId = useMemo(() => {
    if (clientIdProp != null && clientIdProp !== "") {
      return clientIdProp;
    }
    return clientIdFromQuery;
  }, [clientIdProp, clientIdFromQuery]);

  const isDialogMode = typeof onRequestClose === "function";

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(
    !!effectiveClientId && isDialogMode
  );
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const userCredentials = {
    Barcode: user.barcode,
    Role: user.userRoles,
    Organization: user.org,
    Location: user.location_id,
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.client_first_name.trim()) {
      newErrors.client_first_name = "This field is required";
    }

    if (!formData.client_last_name.trim()) {
      newErrors.client_last_name = "This field is required";
    }

    if (!formData.client_mobile_phone_number) {
      newErrors.client_mobile_phone_number = "This field is required";
    } else if (
      formData.client_mobile_phone_number.length < 7 ||
      formData.client_mobile_phone_number.length > 12
    ) {
      newErrors.client_mobile_phone_number =
        "Please enter a valid phone number";
    }

    if (
      formData.client_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)
    ) {
      newErrors.client_email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      client_mobile_phone_number: phone,
    }));
    if (errors.client_mobile_phone_number) {
      setErrors((prev) => ({
        ...prev,
        client_mobile_phone_number: "",
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      client_birthday: date,
    }));
    if (errors.client_birthday) {
      setErrors((prev) => ({
        ...prev,
        client_birthday: "",
      }));
    }
  };

  useEffect(() => {
    if (!effectiveClientId) return;

    const fetchClientByID = async () => {
      if (isDialogMode) setFetching(true);
      else setLoading(true);
      try {
        const response = await baseAxios.get(`clients/${effectiveClientId}`);
        const clientData = response.data.client;
        if (clientData) {
          setFormData({
            client_first_name: clientData.client_first_name || "",
            client_last_name: clientData.client_last_name || "",
            client_email: clientData.client_email || "",
            client_mobile_phone_number:
              clientData.client_mobile_phone_number || "",
            // client_birthday is a DATE serialized as a UTC ISO string
            // (e.g. "1904-07-06T00:00:00.000Z"). Parse only the date part so
            // it isn't shifted a day earlier in sub-UTC (US/CA) timezones.
            client_birthday: clientData.client_birthday
              ? String(clientData.client_birthday).length <= 5
                ? dayjs(`1904/${clientData.client_birthday}`)
                : dayjs(String(clientData.client_birthday).slice(0, 10))
              : null,
            client_id: clientData.client_id || "",
            appointment_notify_opt_in:
              !!clientData.client_appointment_notify_opt_in,
            marketing_opt_in: !!clientData.client_marketing_opt_in,
          });
        }
      } catch {
        toast.error("Failed to fetch client details", { duration: 3000 });
      } finally {
        if (isDialogMode) setFetching(false);
        else setLoading(false);
      }
    };

    fetchClientByID();
  }, [effectiveClientId, isDialogMode]);

  const handleSuccess = useCallback(() => {
    if (typeof onSaved === "function") {
      onSaved();
      return;
    }
    setTimeout(() => {
      navigate("/edit-client");
    }, 1000);
  }, [navigate, onSaved]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (
        !userCredentials.Role.includes(6) &&
        !userCredentials.Role.includes(3) &&
        !userCredentials.Role.includes(2) &&
        !userCredentials.Role.includes(1)
      ) {
        toast.error("User Not authorized, please check your role", {
          duration: 5000,
        });
        return;
      }

      setLoading(true);

      try {
        const payload = {
          client_first_name: formData.client_first_name,
          client_last_name: formData.client_last_name,
          client_email: formData.client_email,
          client_mobile_phone_number: formData.client_mobile_phone_number,
          client_birthday:
            formData.client_birthday &&
            dayjs(formData.client_birthday).isValid()
              ? "1904/" + dayjs(formData.client_birthday).format("MM/DD")
              : null,
          appointment_notify_opt_in: formData.appointment_notify_opt_in,
          marketing_opt_in: formData.marketing_opt_in,
        };

        const response = await baseAxios.patch(
          `clients/${formData.client_id}`,
          payload
        );
        if (response.status === 200) {
          toast.success("Client updated", { duration: 3000 });
          handleSuccess();
        }
      } catch (error) {
        toast.error(
          error?.data?.message ||
            error?.response?.data?.message ||
            "Client update failed, please try again",
          { duration: 5000 }
        );
      } finally {
        setLoading(false);
      }
    },
    [formData, validateForm, userCredentials.Role, handleSuccess]
  );

  const busy = loading || fetching;

  const formFields = (
    <Stack spacing={2}>
      <TextField
        label="First name"
        value={formData.client_first_name}
        onChange={handleInputChange("client_first_name")}
        required
        fullWidth
        size="small"
        disabled={busy}
        error={Boolean(errors.client_first_name)}
        helperText={errors.client_first_name}
        color="primary"
      />

      <TextField
        label="Last name"
        value={formData.client_last_name}
        onChange={handleInputChange("client_last_name")}
        required
        fullWidth
        size="small"
        disabled={busy}
        error={Boolean(errors.client_last_name)}
        helperText={errors.client_last_name}
        color="primary"
      />

      <DatePicker
        label="Birth date"
        value={formData.client_birthday}
        onChange={handleDateChange}
        views={["month", "day"]}
        format="MM/DD"
        disabled={busy}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error: Boolean(errors.client_birthday),
            helperText: errors.client_birthday,
            color: "primary",
          },
        }}
      />

      <TextField
        label="Email"
        type="email"
        value={formData.client_email}
        onChange={handleInputChange("client_email")}
        fullWidth
        size="small"
        disabled={busy}
        error={Boolean(errors.client_email)}
        helperText={errors.client_email}
        color="primary"
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.appointment_notify_opt_in}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  appointment_notify_opt_in: e.target.checked,
                }))
              }
              disabled={busy}
              color="primary"
            />
          }
          label="Sign up for appointment notifications"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.marketing_opt_in}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  marketing_opt_in: e.target.checked,
                }))
              }
              disabled={busy}
              color="primary"
            />
          }
          label="Sign up for marketing communications"
        />
      </Box>

      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.75, fontWeight: 500 }}
        >
          Phone number <Box component="span" color="error.main">*</Box>
        </Typography>
        <PhoneInput
          country="us"
          onlyCountries={["us", "ca"]}
          placeholder="+1 (701) 234-5678"
          value={formData.client_mobile_phone_number}
          onChange={handlePhoneChange}
          disabled={busy}
          inputStyle={{
            width: "100%",
            height: "40px",
            border: errors.client_mobile_phone_number
              ? "2px solid #d32f2f"
              : "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "6px",
            fontSize: "15px",
            outline: "none",
          }}
          buttonStyle={{
            height: "40px",
            borderRadius: "6px 0 0 6px",
          }}
          containerStyle={{
            width: "100%",
          }}
        />
        {errors.client_mobile_phone_number && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
            {errors.client_mobile_phone_number}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isDialogMode ? (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              bgcolor: "secondary.main",
              color: "common.white",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              px: 3,
              py: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="inherit">
                Edit client
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
              >
                Update client details and consent preferences.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={onRequestClose}
              disabled={busy}
              size="small"
              sx={{
                color: "primary.main",
                mt: 0.25,
                "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {fetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            ) : (
              <Box
                component="form"
                id={FORM_ID}
                onSubmit={onSubmit}
                sx={{ p: 3 }}
              >
                {formFields}
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              gap: 1,
            }}
          >
            <Button
              onClick={onRequestClose}
              disabled={busy}
              variant="outlined"
              color="secondary"
              sx={{ textTransform: "none", minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="contained"
              color="primary"
              disabled={busy || fetching}
              sx={{
                minWidth: 140,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Save client"
              )}
            </Button>
          </DialogActions>
        </>
      ) : (
        <SingleCard title="Edit Client">
          <Box component="form" onSubmit={onSubmit} sx={{ p: 1 }}>
            {formFields}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={busy}
              sx={{
                mt: 2.5,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Update Client Information"
              )}
            </Button>
          </Box>
        </SingleCard>
      )}
    </LocalizationProvider>
  );
}
